import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiClient, ApiResponse, isApiError, type ApiPaginatedResponse } from "./_client";
import { type FeedbackStatus } from "./answer-feedback";
import { type GraphMetadata } from "./graph-metadata";
import { OutputTypeId, type Question } from "./q-and-a";
import { type ColumnSort } from "@tanstack/react-table";
import { AxiosResponse } from "axios";
import { downloadBlob } from "@/lib/utils";

// Query key factory for analyses - follows TkDodo's recommended pattern
// https://tkdodo.eu/blog/effective-react-query-keys
export const analysisKeys = {
  all: ["analyses"] as const,
  lists: () => [...analysisKeys.all, "list"] as const,
  list: (filters: {
    pageSize?: number;
    pageIndex?: number;
    filter?: string;
    ordering?: ColumnSort | string;
    projectId?: string;
    portfolioId?: string;
    isTemplate?: boolean;
    sourceTemplateId?: string;
  }) =>
    [
      ...analysisKeys.lists(),
      filters.pageSize,
      filters.pageIndex,
      filters.filter,
      filters.ordering,
      filters.projectId,
      filters.portfolioId,
      filters.isTemplate,
      filters.sourceTemplateId,
    ] as const,
  details: () => [...analysisKeys.all, "detail"] as const,
  detail: (id: string) => [...analysisKeys.details(), id] as const,

  // Sections are nested under an analysis
  sections: (analysisId: string) =>
    [...analysisKeys.detail(analysisId), "sections"] as const,
  sectionList: (
    analysisId: string,
    filters?: {
      pageSize?: number;
      pageIndex?: number;
      filter?: string;
      ordering?: ColumnSort | string;
    },
  ) =>
    [
      ...analysisKeys.sections(analysisId),
      filters?.pageSize,
      filters?.pageIndex,
      filters?.filter,
      filters?.ordering,
    ] as const,

  // Questions within a section
  sectionQuestions: (analysisId: string, sectionId: string) =>
    [...analysisKeys.sections(analysisId), sectionId, "questions"] as const,

  // Reports are nested under an analysis
  reports: (analysisId: string) =>
    [...analysisKeys.detail(analysisId), "reports"] as const,
  reportList: (
    analysisId: string,
    filters?: {
      pageSize?: number;
      pageIndex?: number;
      filter?: string;
      sorting?: ColumnSort | string;
    },
  ) =>
    [
      ...analysisKeys.reports(analysisId),
      filters?.pageSize,
      filters?.pageIndex,
      filters?.filter,
      filters?.sorting,
    ] as const,
  report: (analysisId: string, reportId: string) =>
    [...analysisKeys.reports(analysisId), reportId] as const,
  // True for any cached key in the reports family (list, status, detail).
  // Owned here so cache-splice predicates can't silently drift from the
  // factory's key shape; callers must still shape-check the payload
  // (only report detail carries question_map).
  isReportsKey: (key: readonly unknown[]) =>
    key[0] === "analyses" && key.includes("reports"),
  reportStatus: (analysisId: string) =>
    [...analysisKeys.reports(analysisId), "status"] as const,
  drift: (analysisId: string) =>
    [...analysisKeys.detail(analysisId), "template-drift"] as const,
  derivedDriftSummary: (templateId: string) =>
    [...analysisKeys.detail(templateId), "derived-drift-summary"] as const,
};

export const SCOPES = ["project", "portfolio"] as const;
export type ScopeLevel = (typeof SCOPES)[number];

export type MinimalTag = {
  id: string;
  name: string;
};

export type MinimalCategory = {
  id: string;
  name: string;
};

// Minimal question data for display in section lists - no nested relationships
export type SectionQuestion = {
  id: string;
  question: string;
  variable_name: string;
  output_type: OutputTypeId;
  extra_prompt: string | null;
  is_rule_generated?: boolean;
};

export type AnalysisSection = {
  id: string;
  title: string;
  description: string;
  analysis_id: string;
  questions: SectionQuestion[];
};

export type Analysis = {
  id: string;
  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
  question_prompts_updated_at?: string | null; // ISO datetime
  answer_state_updated_at?: string | null; // ISO datetime
  question_prompts_changed_since_last_run?: boolean;
  answer_state_changed_since_last_run?: boolean;
  last_run: string; // ISO datetime
  created_by: string;
  title: string;
  description: string;
  scope: {
    level: ScopeLevel;
    ids: string[];
  };
  is_template: boolean;
  source_template_id?: string | null;
  source_template_title?: string | null;
  derived_count?: number;
  rule_set_ids?: string[]; // Only present in detail response
  is_generating?: boolean; // True if analysis is being populated by background task
};

export type AnalysisEditData = Omit<
  Analysis,
  | "sections"
  | "updated_at"
  | "created_at"
  | "created_by"
  | "last_run"
  | "is_template"
>;
export type AnalysisCreateData = Omit<AnalysisEditData, "id">;

type AnalysisPaginatedResponse = ApiPaginatedResponse<Analysis>;

export const useAnalyses = (
  pageSize: number = 10,
  pageIndex: number = 1,
  filter: string,
  ordering: ColumnSort | string,
  projectId?: string,
  portfolioId?: string,
  isTemplate?: boolean,
  sourceTemplateId?: string,
): UseQueryResult<AnalysisPaginatedResponse> => {
  let url = `api/documents/analyses/?page_size=${pageSize}&page=${pageIndex}`;
  if (filter) {
    url += `&search=${filter}`;
  }
  if (ordering) {
    if (typeof ordering === "string") {
      url += `&ordering=${ordering}`;
    } else {
      url += `&ordering=${ordering.desc ? "-" : ""}${ordering.id}`;
    }
  }
  if (projectId) {
    url += `&project_id=${projectId}`;
  }
  if (portfolioId) {
    url += `&portfolio_id=${portfolioId}`;
  }
  if (isTemplate !== undefined) {
    url += `&is_template=${isTemplate}`;
  }
  if (sourceTemplateId) {
    url += `&source_template=${sourceTemplateId}`;
  }

  const query = useQuery<AnalysisPaginatedResponse>({
    queryKey: analysisKeys.list({ pageSize, pageIndex, filter, ordering, projectId, portfolioId, isTemplate, sourceTemplateId }),
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<AnalysisPaginatedResponse>
      >(url);
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
  return query;
};

export const useAnalysis = (analysisId?: string): UseQueryResult<Analysis> => {
  const query = useQuery<Analysis>({
    queryKey: analysisKeys.detail(analysisId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<never, AxiosResponse<Analysis>>(
        `api/documents/analyses/${analysisId}/`,
      );
      return response.data;
    },
    enabled: !!analysisId,
    staleTime: 30 * 1000, // 30 seconds
  });
  return query;
};

export const createAnalysis = async (data: AnalysisCreateData) => {
  return await apiClient.post<Analysis>("api/documents/analyses/", data);
};

export const updateAnalysis = async (data: AnalysisEditData) => {
  const response = await apiClient.patch(
    `api/documents/analyses/${data.id}/`,
    data,
  );
  return response;
};

export const detachAnalysisFromTemplate = async (analysisId: string) => {
  return await apiClient.post<Analysis>(
    `api/documents/analyses/${analysisId}/detach-from-template/`,
  );
};

export const saveReportAsTemplate = async (
  analysisId: string,
  data: { title: string; description: string; rule_sets?: string[] },
) => {
  return await apiClient.post<Analysis>(
    `api/documents/analyses/${analysisId}/save-as-template/`,
    data,
  );
};

export const convertReportToTemplate = async (analysisId: string) => {
  return await apiClient.post<Analysis>(
    `api/documents/analyses/${analysisId}/convert-to-template/`,
  );
};

export type TemplateDriftItem = {
  id: string;
  title?: string;
  section_id?: string;
  section_title?: string;
  question?: string;
  variable_name?: string;
  fields?: string[];
};

export type TemplateDrift = {
  has_drift: boolean;
  added_sections: TemplateDriftItem[];
  removed_sections: TemplateDriftItem[];
  updated_sections: TemplateDriftItem[];
  unmatched_sections: TemplateDriftItem[];
  added_questions: TemplateDriftItem[];
  removed_questions: TemplateDriftItem[];
};

export const useTemplateDrift = (
  analysisId: string | undefined,
  enabled: boolean,
): UseQueryResult<TemplateDrift> => {
  return useQuery<TemplateDrift>({
    queryKey: analysisKeys.drift(analysisId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<TemplateDrift>(
        `api/documents/analyses/${analysisId}/template-drift/`,
      );
      if (isApiError(response)) {
        throw new Error(response.error || "Failed to load template drift");
      }
      return response.data;
    },
    enabled: !!analysisId && enabled,
  });
};

export const adoptTemplateChanges = async (
  analysisId: string,
  opts: { remove_unmatched_sections?: boolean } = {},
) => {
  return await apiClient.post<Analysis>(
    `api/documents/analyses/${analysisId}/adopt-template-changes/`,
    opts,
  );
};

export type DerivedDriftRow = {
  id: string;
  title: string;
  scope_label: string | null;
  has_drift: boolean | null;
  drift_status: "drift" | "in_sync" | "unknown";
  drift_error?: string | null;
};

export const useDerivedDriftSummary = (
  templateId: string | undefined,
  enabled: boolean,
): UseQueryResult<DerivedDriftRow[]> => {
  return useQuery<DerivedDriftRow[]>({
    queryKey: analysisKeys.derivedDriftSummary(templateId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<DerivedDriftRow[]>(
        `api/documents/analyses/${templateId}/derived-drift-summary/`,
      );
      if (isApiError(response)) {
        throw new Error(response.error || "Failed to load derived drift");
      }
      return response.data;
    },
    enabled: !!templateId && enabled,
  });
};

export const useApplyDriftToDerived = (templateId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reportIds: string[]) =>
      apiClient.post<{ applied: string[]; skipped: string[] }>(
        `api/documents/analyses/${templateId}/apply-drift-to-derived/`,
        { report_ids: reportIds },
      ),
    onSuccess: (response) => {
      if (isApiError(response)) return;
      queryClient.invalidateQueries({
        queryKey: analysisKeys.derivedDriftSummary(templateId),
      });
      queryClient.invalidateQueries({ queryKey: analysisKeys.lists() });
      queryClient.invalidateQueries({ queryKey: analysisKeys.detail(templateId) });
      response.data.applied.forEach((reportId) => {
        queryClient.invalidateQueries({
          queryKey: analysisKeys.detail(reportId),
        });
        queryClient.invalidateQueries({
          queryKey: analysisKeys.drift(reportId),
        });
      });
    },
  });
};

export const duplicateAnalysis = async (
  analysisId: string,
  data: {
    title: string;
    description: string;
    scope: { level: ScopeLevel; ids: string[] };
    is_template?: boolean;
    rule_sets?: string[];
  },
) => {
  return await apiClient.post<Analysis>(
    `api/documents/analyses/${analysisId}/duplicate/`,
    data,
  );
};

export const deleteAnalysis = async (analysisId: string) => {
  return await apiClient.delete(`api/documents/analyses/${analysisId}/`);
};

export const createSection = async (
  analysisId: string,
  data?: Omit<AnalysisSection, "id" | "questions" | "analysis_id">,
) => {
  return await apiClient.post(
    `api/documents/analyses/${analysisId}/sections/`,
    data,
  );
};

export const updateSection = async (
  data: Omit<AnalysisSection, "questions">,
) => {
  return await apiClient.patch<AnalysisSection>(
    `api/documents/analyses/${data.analysis_id}/sections/${data.id}/`,
    data,
  );
};

export const addQuestionsToSection = async (
  section: AnalysisSection,
  questionIds: string[],
) => {
  return await apiClient.post(
    `api/documents/analyses/${section.analysis_id}/sections/${section.id}/add-question/`,
    { ids: questionIds },
  );
};

export const duplicateQuestionInSection = async (
  section: AnalysisSection,
  questionId: string,
) => {
  const response = await apiClient.post(
    `api/documents/analyses/${section.analysis_id}/sections/${section.id}/duplicate-question/`,
    { id: questionId },
  );
  return response.data;
};

export const removeQuestionFromSection = async (
  section: AnalysisSection,
  questionId: string,
) => {
  return await apiClient.post(
    `api/documents/analyses/${section.analysis_id}/sections/${section.id}/remove-question/`,
    { id: questionId },
  );
};

export const deleteSection = async (analysisId: string, sectionId: string) => {
  return await apiClient.delete(
    `api/documents/analyses/${analysisId}/sections/${sectionId}/`,
  );
};

type UseAnalysisSectionsOptions = {
  pageSize?: number;
  pageIndex?: number;
  filter?: string;
  ordering?: ColumnSort | string;
};

export const useAnalysisSections = (
  analysisId?: string,
  options: UseAnalysisSectionsOptions = {},
): UseQueryResult<AnalysisSection[]> => {
  const {
    pageSize = undefined,
    pageIndex = 1,
    filter = "",
    ordering = undefined,
  } = options;

  let url = `api/documents/analyses/${analysisId}/sections/?&page=${pageIndex}`;
  if (pageSize) {
    url += `&page_size=${pageSize}`;
  }
  if (filter) {
    url += `&search=${filter}`;
  }
  if (ordering) {
    if (typeof ordering === "string") {
      url += `&ordering=${ordering}`;
    } else {
      url += `&ordering=${ordering.desc ? "-" : ""}${ordering.id}`;
    }
  }

  const query = useQuery<AnalysisSection[]>({
    queryKey: analysisKeys.sectionList(analysisId ?? "", {
      pageSize,
      pageIndex,
      filter,
      ordering,
    }),
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<AnalysisSection[]>
      >(url);
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!analysisId,
  });
  return query;
};

export const useSectionQuestions = (
  analysisId?: string,
  sectionId?: string,
): UseQueryResult<SectionQuestion[]> => {
  const url = `api/documents/analyses/${analysisId}/sections/${sectionId}/questions/`;

  return useQuery<SectionQuestion[]>({
    queryKey: analysisKeys.sectionQuestions(analysisId ?? "", sectionId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<SectionQuestion[]>
      >(url);
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled: Boolean(analysisId && sectionId),
  });
};

type ReportSection = {
  id: string;
  title: string;
  description: string;
  questions: string[];
};

type ReportQuestion = {
  source_question_id: string | null;
  edit_context?: {
    kind: "template_run" | "derived_report" | "standalone_report";
    template_title?: string | null;
    report_title?: string | null;
  };
  is_rule_generated?: boolean;
  variable_name: string;
  output_type: OutputTypeId;
  prompt: string;
  extra_prompt: string | null;
};

export type QuestionMap = {
  [key: string]: ReportQuestion & {
    answers: ReportAnswer[];
  };
};

type ProjectInfo = {
  id: string;
  name: string;
};

export type ReportAnswerCitation = {
  citation: string | null;
  page: number | null;
  document_id: string | null;
  filename: string | null;
  source_project_id: string | null;
};

export type ReportAnswer = {
  source_answer_id: string | null;
  source_question_id: string | null;
  answer_group_id: string | null;
  project: ProjectInfo | null;
  portfolio: ProjectInfo | null;
  value: string;
  citations: ReportAnswerCitation[];
  // Grounded citations (id + page + [N] marker per document) — lets the report's
  // Graph Full dialog deep-link each chip to its own citation. `marker` is null
  // on older answers (dialog falls back to document-first). Empty for non-graph.
  graph_grounded_citations?: {
    id: string;
    document_id: string;
    page: number | null;
    marker?: number | null;
  }[];
  retrieved_at: string; // ISOformat datetime
  extracted_from_project: boolean;
  extracted_from_document: boolean;
  document_info: {
    document_id: string | null;
    document_link: string | null;
    link_type: "document" | "project_data" | null;
    filename: string | null;
    document_drd: string | null;
  };
  // Hallucination detection metadata (optional - may not be present in older reports)
  grounding_confidence?: number | null;
  hallucination_flags?: string[];
  // Most-recent system-inferred AnswerGroup for this question/scope (optional)
  is_current_value?: boolean | null;
  /** Org-wide review verdict; null = unreviewed. Server sends null for
   *  users without org-wide feedback read access. */
  latest_feedback_status?: FeedbackStatus | null;
  // Time-series detection (optional - may not be present in older reports)
  is_time_series?: boolean;
  time_series_label?: string;
  time_series_order?: number | null;
  time_series_period_start?: string | null;
  time_series_period_end?: string | null;
  time_series_unit?: string;
  // Signature data (optional - present for signature detection questions)
  signature_data?: Array<{
    signer_name: string;
    raw_signer_name: string;
    signer_role: string;
    signer_company: string;
    signed: boolean;
    confidence: number;
    page?: number | null;
    method?: string;
    source_document_id?: string;
    filename?: string;
  }>;
  graph_metadata?: GraphMetadata;
};

export type MissingDataItem = {
  question_id: string;
  text: string;
};

export type MissingRuleData = {
  name: string;
  variables: string[];
};

export type MissingData = {
  projects: Record<string, MissingDataItem[]>;
  rules: Record<string, MissingRuleData>;
};

export type DocumentRecommendations = {
  by_variable: Record<string, string[]>; // question_id -> array of document titles
};

// TEMP we may remove this
export type AnswersAlgorithm = "global_rag" | "original";

type ReportScope = {
  level: ScopeLevel;
  ids: string[];
  algorithm?: AnswersAlgorithm;
};

// Lightweight report data for list views - doesn't include heavy question_map
export type AnalysisReportListItem = {
  id: string;
  analysis_id: string;
  title: string;
  description: string;
  created_at: string;
  status?: "running" | "completed" | "failed";
  error_message?: string;
  scope?: ReportScope;
};

// Full report data for detail view - includes complete question_map
export type AnalysisReport = {
  id: string;
  analysis_id: string;
  title: string;
  description: string;
  created_at: string;
  section_data: ReportSection[];
  question_map: QuestionMap;
  missing_data?: MissingData;
  document_recommendations?: DocumentRecommendations;
  status?: "running" | "completed" | "failed";
  error_message?: string;
  scope?: ReportScope;
  pending_rule_generation_task_id?: string | null;
};

type AnalysisReportPaginatedResponse =
  ApiPaginatedResponse<AnalysisReportListItem>;

export interface AnalysisReportStatus {
  can_run_analysis: boolean;
  validation_message: string;
  reason?:
    | "REPORTS"
    | "PROJECTS"
    | "DOCUMENTS"
    | "AI_DISABLED"
    | "AI_COST_EXCEEDED"
    | null;
  running_reports_count?: number;
  processing_documents_count?: number;
  active_projects_count?: number;
  active_project_names?: string[];
}

type UseAnalysisReportsOptions = {
  pageSize?: number;
  pageIndex?: number;
  filter?: string;
  sorting?: ColumnSort | string;
};

export const fetchAnalysisReports = async (
  analysisId: string,
  options: UseAnalysisReportsOptions = {},
): Promise<AnalysisReportPaginatedResponse> => {
  const {
    pageSize = 10,
    pageIndex = 1,
    filter = "",
    sorting: ordering = undefined,
  } = options;

  let url = `api/documents/analyses/${analysisId}/reports/?page_size=${pageSize}&page=${pageIndex}`;
  if (filter) {
    url += `&search=${encodeURIComponent(filter)}`;
  }
  if (ordering) {
    if (typeof ordering === "string") {
      url += `&ordering=${ordering}`;
    } else {
      url += `&ordering=${ordering.desc ? "-" : ""}${ordering.id}`;
    }
  }

  const response = await apiClient.get<
    never,
    AxiosResponse<AnalysisReportPaginatedResponse>
  >(url);
  return response.data;
};

export const useAnalysisReports = (
  analysisId: string,
  options: UseAnalysisReportsOptions = {},
): UseQueryResult<AnalysisReportPaginatedResponse> => {
  const {
    pageSize = 10,
    pageIndex = 1,
    filter = "",
    sorting: ordering = undefined,
  } = options;

  const query = useQuery<AnalysisReportPaginatedResponse>({
    queryKey: analysisKeys.reportList(analysisId, {
      pageSize,
      pageIndex,
      filter,
      sorting: ordering,
    }),
    queryFn: () =>
      fetchAnalysisReports(analysisId, { pageSize, pageIndex, filter, sorting: ordering }),
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!analysisId,
  });
  return query;
};

export const useAnalysisReport = (
  analysisId?: string,
  reportId?: string,
): UseQueryResult<AnalysisReport> => {
  const query = useQuery<AnalysisReport>({
    queryKey: analysisKeys.report(analysisId ?? "", reportId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<AnalysisReport>
      >(`api/documents/analyses/${analysisId}/reports/${reportId}/`);
      return response.data;
    },
    enabled: Boolean(analysisId && reportId),
    staleTime: 30 * 1000, // 30 seconds
  });
  return query;
};

export const runAnalysisReport = async (
  analysisId: string,
  algorithm?: AnswersAlgorithm,
) => {
  let url = `api/documents/analyses/${analysisId}/run/`;
  if (algorithm) {
    url += `?algorithm=${algorithm}`;
  }
  return await apiClient.post<Analysis>(url);
};

export const deleteAnalysisReport = async (
  analysisId: string,
  reportId: string,
) => {
  return await apiClient.delete(
    `api/documents/analyses/${analysisId}/reports/${reportId}/`,
  );
};

export const getAnalysisReportStatus = async (
  analysisId: string,
): Promise<AnalysisReportStatus> => {
  const response = await apiClient.get<
    never,
    AxiosResponse<AnalysisReportStatus>
  >(`api/documents/analyses/${analysisId}/report-status/`);
  return response.data;
};

export const exportAnalysisTemplate = async ({
  analysisId = undefined,
}: {
  analysisId?: string;
}) => {
  let url = `api/documents/analyses/export/`;
  if (analysisId) {
    url += `?id=${analysisId}`;
  }
  const response = await apiClient.get(url, {
    responseType: "blob",
  });
  if (response.data) {
    downloadBlob(
      response.data,
      "analyses_export.xlsx",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
  }

  return response;
};

type BulkUploadAnalysisTemplateResponse = ApiResponse<
  {
    created_analyses: Analysis[];
    updated_analyses: Analysis[];
    created_sections: AnalysisSection[];
    updated_sections: AnalysisSection[];
    created_questions: Question[];
  },
  { bulkfile: string }
>;

export const bulkUploadAnalysisTemplate = async ({
  file,
}: {
  file: File;
}): Promise<BulkUploadAnalysisTemplateResponse> => {
  const form = new FormData();
  form.append("bulkfile", file);
  const options = {
    method: "POST",
    url: `/api/documents/analyses/import/`,
    headers: { "Content-Type": "multipart/form-data" },
    data: form,
  };
  const response = await apiClient.request(options);
  return response;
};

export const exportReportXLSX = async (
  analysisId: string,
  reportId: string,
): Promise<Blob> => {
  const response = await apiClient.get(
    `/api/documents/analyses/${analysisId}/reports/${reportId}/export-xlsx/`,
    {
      responseType: "blob",
    },
  );
  return response.data;
};

export type RFIGenerationRequest = {
  selected_recommendations?: Record<string, number[]>; // Optional - for legacy recommendations tab
  selected_missing_data?: Record<string, MissingDataItem[]>;
  include_validation_issues?: boolean;
  include_all_missing_data?: boolean; // New - includes all missing data from report
};

export type RFIGenerationResponse = {
  file_url: string;
  filename: string;
};

export const generateRFI = async (
  analysisId: string,
  reportId: string,
  request: RFIGenerationRequest,
): Promise<Blob> => {
  const response = await apiClient.post(
    `/api/documents/analyses/${analysisId}/reports/${reportId}/generate-rfi/`,
    request,
    {
      responseType: "blob",
    },
  );
  return response.data;
};
